import { useState, useEffect, useCallback } from "react";
import { avatarBg } from "@/lib/avatar";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { useAppMode } from "@/context/AppModeContext";
import { useLocale } from "@/i18n/LocaleContext";
import { toast } from "sonner";
import { MessageCircle, ArrowLeft } from "lucide-react";
import Navigation from "@/components/Navigation";
import { onRealtimeEvent } from "@/lib/realtime";

export default function Chat() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { mode, appName } = useAppMode();
  const { t } = useLocale();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await api.get("/messages/conversations");
      setConversations(response.data.conversations);
    } catch (error) {
      console.error("Failed to load conversations:", error);
      toast.error("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    const unsubscribe = onRealtimeEvent((event) => {
      if (event.type === "new_message") {
        fetchConversations();
      }
    });
    return unsubscribe;
  }, [fetchConversations]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="chat-page">
      <Navigation />
      
      <main className="pt-20 pb-24 px-4 md:px-6">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <h1 className="text-2xl font-bold font-['Outfit'] tracking-tight">{t("chat.title")}</h1>
            <span className="text-xs px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary" data-testid="inbox-mode-badge">
              {t("chat.inboxBadge").replace("{app}", appName)}
            </span>
          </div>

          {conversations.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <MessageCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">{t("chat.empty")}</h2>
              <p className="text-muted-foreground mb-6">
                {t("chat.emptySub")}
              </p>
              <button
                onClick={() => navigate("/discover")}
                className="text-primary hover:underline font-medium"
              >
                {t("chat.discoverProfiles")}
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {conversations.map((conv) => (
                <div
                  key={conv.conversation_id}
                  className="glass-card p-4 flex items-center gap-4 cursor-pointer hover:bg-white/10 transition-colors"
                  onClick={() => navigate(`/chat/${conv.conversation_id}`)}
                  data-testid={`conversation-${conv.conversation_id}`}
                >
                  <div className="relative">
                    <img
                      src={conv.other_user?.photos?.[0] || conv.other_user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(conv.other_user?.name || "User")}&background=${avatarBg()}&color=fff&size=100`}
                      alt={conv.other_user?.name}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                    {conv.unread_count > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full text-xs flex items-center justify-center font-bold">
                        {conv.unread_count}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold truncate">{conv.other_user?.name}</h3>
                      <span className="text-xs text-muted-foreground">
                        {conv.last_message?.created_at ? 
                          new Date(conv.last_message.created_at).toLocaleDateString() : ""}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {conv.last_message?.content || t("chat.startConvo")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
